import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { Formik } from 'formik';

import { LoadingButton } from '@mui/lab';
import { Rating, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, TextField, InputLabel } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

import feedbackApi from '../../../services/api/FeedbackApi';
import useAuth from '../../../hooks/useAuth';

const SignupSchema = Yup.object({
  feedback: Yup.string().max(500, 'Max length is 500 characters').required('Feedback is required'),
});

const FeedbackFormDialog = ({ isOpen, closeDialog, recordId, belongsTo, objectTitle, sourceSystemID }) => {
  const { username } = useAuth();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const cancelHanler = () => {
    if (formRef.current) {
      formRef.current.resetForm();
    }

    closeDialog();
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={{
        rating: 5,
        feedback: '',
      }}
      validationSchema={SignupSchema}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          setSubmitting(true);

          await feedbackApi.addNewComment({
            user: username,
            sourceSystemID,
            recordID: recordId,
            belongsTo,
            comment: values.feedback,
            rating: +values.rating,
            rating2: 0,
            rating3: 0,
            active: true,
            flags: 0,
            createdOn: new Date().toISOString(),
            createdBy: username,
          });

          resetForm();
          setSubmitting(false);
          closeDialog();
          toast.success(t('Info_Message_SaveSuccessfully'));
        } catch (error) {
          console.log(error);
          setSubmitting(false);
          toast.error(t('Error_Message_SaveError'));
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <>
          <Dialog open={isOpen} fullWidth maxWidth="sm">
            <DialogTitle>
              {t('Dialog_Title_FeedbackHeader')} [{`${objectTitle}`}]
            </DialogTitle>
            <Divider />
            <DialogContent>
              <Grid container spacing={2} padding={2}>
                <Grid item xs={12} sm={6} md={4} lg={3} sx={{ ml: 1 }}>
                  <InputLabel>{t('Label_Rating')}</InputLabel>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Rating name="rating" value={+values.rating} onChange={handleChange} />
                </Grid>
              </Grid>

              <Grid container spacing={2} padding={2}>
                <Grid item xs={12} sm={12} md={12} lg={12} sx={{ pt: 0 }}>
                  <TextField
                    fullWidth
                    label={t('Label_Feedback')}
                    size="small"
                    multiline
                    rows={4}
                    name="feedback"
                    placeholder="Please add comments.."
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.feedback}
                    error={Boolean(touched.feedback && errors.feedback)}
                    helperText={touched.feedback && errors.feedback}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <LoadingButton
                color="primary"
                size="small"
                startIcon={<SaveIcon size="small" />}
                variant="contained"
                loading={isSubmitting}
                loadingPosition="start"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {t('Btn_Submit')}
              </LoadingButton>

              <Button autoFocus variant="outlined" onClick={cancelHanler}>
                {t('Btn_Cancel')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Formik>
  );
};

export default FeedbackFormDialog;
